// ============================================================================
// Module Analysis: Flexible & Customizable Dashboard Types
// ============================================================================

export type AnalysisChartType =
  | 'bar'
  | 'horizontal_bar'
  | 'line'
  | 'area'
  | 'pie'
  | 'donut'
  | 'radar'
  | 'scatter'
  | 'kpi'
  | 'gauge'
  | 'table';

export type AnalysisDataSource =
  | 'occurrences'
  | 'reports'
  | 'investigations'
  | 'audits'
  | 'actions'
  | 'assets'
  | 'asir_occurrences'
  | 'quality_assurance'
  | 'risk_management'
  | 'audits_dashboard'
  | 'findings_dashboard'
  | 'spi_kpi_monitoring'
  | 'missing_spi_data';

export type AnalysisAggregation = 'COUNT' | 'SUM' | 'AVG' | 'MEDIAN' | 'MIN' | 'MAX';

export type AnalysisColorPalette =
  | 'aviation_blue'
  | 'safety_high_contrast'
  | 'emerald_precision'
  | 'sunset_flare'
  | 'monochrome_slate'
  | 'custom';

export interface CustomAnalysisDashboard {
  id: string;
  organizationId: string;
  dashboardCode: string;
  title: string;
  description?: string;
  icon?: string;
  isDefault: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  widgets?: CustomAnalysisWidget[];
  widgetCount?: number;
}

export interface CustomAnalysisWidget {
  id: string;
  dashboardId: string;
  organizationId: string;
  title: string;
  chartType: AnalysisChartType;
  dataSource: AnalysisDataSource;
  dimension: string;
  metric: string;
  aggregation: AnalysisAggregation;
  widthCol: number; // 3 (1/4), 4 (1/3), 6 (1/2), 8 (2/3), 12 (full)
  heightPx: number; // 280 to 520
  sortOrder: number;
  colorPalette: AnalysisColorPalette | string;
  customColor?: string;
  fontSize: 'sm' | 'md' | 'lg';
  showLegend: boolean;
  showGrid: boolean;
  showLabels: boolean;
  targetThreshold?: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  enableCrossFilter: boolean;
  configJson?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDashboardDTO {
  title: string;
  description?: string;
  icon?: string;
  dashboardCode?: string;
}

export interface UpdateDashboardDTO {
  title?: string;
  description?: string;
  icon?: string;
}

export interface CreateWidgetDTO {
  title: string;
  chartType: AnalysisChartType;
  dataSource: AnalysisDataSource;
  dimension: string;
  metric?: string;
  aggregation?: AnalysisAggregation;
  widthCol?: number;
  heightPx?: number;
  sortOrder?: number;
  colorPalette?: AnalysisColorPalette | string;
  customColor?: string;
  fontSize?: 'sm' | 'md' | 'lg';
  showLegend?: boolean;
  showGrid?: boolean;
  showLabels?: boolean;
  targetThreshold?: number;
  warningThreshold?: number;
  criticalThreshold?: number;
  enableCrossFilter?: boolean;
  configJson?: Record<string, any>;
}

export interface UpdateWidgetDTO extends Partial<CreateWidgetDTO> {}

export interface AnalysisQueryFilter {
  dateRange?: 'all' | '30d' | '90d' | 'year' | 'custom';
  startDate?: string;
  endDate?: string;
  severity?: string;
  department?: string;
  status?: string;
  aircraftReg?: string;
  crossFilters?: Record<string, string>;
}

export interface AnalysisDataPoint {
  label: string;
  value: number;
  secondaryValue?: number;
  count?: number;
  subLabel?: string;
  status?: 'NORMAL' | 'WARNING' | 'CRITICAL';
  color?: string;
}

export interface AnalysisQueryResult {
  widgetId?: string;
  data: AnalysisDataPoint[];
  series?: AnalysisDataPoint[];
  total: number;
  totalCount?: number;
  thresholdStatus?: 'ok' | 'normal' | 'warning' | 'critical' | 'NORMAL' | 'WARNING' | 'CRITICAL';
  summary?: {
    currentValue?: number;
    deltaPercent?: number;
    thresholdStatus?: 'NORMAL' | 'WARNING' | 'CRITICAL';
    targetValue?: number;
    warningThreshold?: number;
    criticalThreshold?: number;
  };
}

export interface DataSourceFieldDefinition {
  field: string;
  label: string;
  type: 'dimension' | 'metric';
  description?: string;
  supportedAggregations?: AnalysisAggregation[];
  defaultAggregation?: AnalysisAggregation;
  unit?: string;
}

export interface DataSourceCatalogItem {
  id: AnalysisDataSource;
  name: string;
  description: string;
  dimensions: DataSourceFieldDefinition[];
  metrics: DataSourceFieldDefinition[];
}

export const PALETTE_COLORS: Record<string, string[]> = {
  aviation_blue: ['#0284c7', '#38bdf8', '#0369a1', '#7dd3fc', '#bae6fd', '#075985'],
  safety_high_contrast: ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'],
  emerald_precision: ['#059669', '#10b981', '#34d399', '#6ee7b7', '#047857', '#a7f3d0'],
  sunset_flare: ['#f97316', '#fb923c', '#ea580c', '#fdba74', '#c2410c', '#fed7aa'],
  monochrome_slate: ['#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#1e293b'],
};

export function getPaletteColor(palette: string = 'aviation_blue', index: number = 0, customColor?: string): string {
  if (customColor) return customColor;
  const list = PALETTE_COLORS[palette] || PALETTE_COLORS['aviation_blue'];
  return list[index % list.length];
}

export interface DrilldownRecordItem {
  id: string;
  recordNumber: string;
  title: string;
  date?: string;
  severity?: string;
  status?: string;
  category?: string;
  department?: string;
  location?: string;
  description?: string;
}
